<template>
  <div class="content">
    <h1 class="has-text-centered">Das internationale Einheitensystem (SI)</h1>
    <div class="box">
      <p>
        Das <strong>internationale Einheitensystem</strong> oder <strong>SI</strong> (frz. <i>Système international d’unités</i>) ...
        <ul>
          <li>... ist das am <b>weitesten verbreitete Einheitensystem für physikalische Größen</b></li>
          <li>... hat <b>7 Basiseinheiten</b></li>
          <li>... hat <b>22 abgeleitete Einheiten</b> mit eigenen Namen und Formelzeichen</li>
          <li>... ist <b>dezimal</b></li>
          <li>... ein <b>metrisches Einheitensystem</b></li>
          <li>... und ein <b><a target="_blank" href="https://de.wikipedia.org/wiki/Internationales_Einheitensystem#Koh%C3%A4rente_SI-Einheiten">kohärentes Einheitensystem</a></b></li>
        </ul>
      </p>
      <p>
        Alle Einheiten des internationalen Einheitensystems (Basiseinheiten und abgeleitete Einheiten) <b>können miteinander kombiniert werden</b>.<br>
        Um sehr große oder sehr kleine Zahlen besser darstellen zu können, werden SI-Einheiten oft in Kombination mit <router-link :to="{name: 'tables-units-prefixes'}">Einheitenvorsätzen</router-link> (SI-Präfixe) eingesetzt.
      </p>
    </div>
    <h2 class="has-text-centered">Basiseinheiten</h2>
    <div class="columns">
      <div class="column is-one-third">
        <div class="box is-wordWrapBreakWord is-fullHeight has-text-centered">
          <h3>Länge</h3>
          <p>
            <strong>Formelzeichen: </strong> <span v-katex="'l'"></span><br>
            <strong>Einheit: </strong> <span v-katex="'m'"></span> (Meter)
          </p>
          <p>
            Länge der Strecke, die das Licht im Vakuum während der Dauer von 1/299.792.458 Sekunden zurücklegt.
          </p>
        </div>
      </div>
      <div class="column is-one-third">
        <div class="box is-wordWrapBreakWord is-fullHeight has-text-centered">
          <h3>Masse</h3>
          <p>
            <strong>Formelzeichen: </strong> <span v-katex="'m'"></span><br>
            <strong>Einheit: </strong> <span v-katex="'kg'"></span> (Kilogramm)
          </p>
          <p>
            Das Kilogramm ist gleich der Masse des <a target="_blank" href="https://de.wikipedia.org/wiki/Kilogramm#Urkilogramm">Internationalen Kilogrammprototyps.</a>
          </p>
        </div>
      </div>
      <div class="column is-one-third">
        <div class="box is-wordWrapBreakWord is-fullHeight has-text-centered">
          <h3>Zeit</h3>
          <p>
            <strong>Formelzeichen: </strong> <span v-katex="'t'"></span><br>
            <strong>Einheit: </strong> <span v-katex="'s'"></span> (Sekunden)
          </p>
          <p>
            Das 9.192.631.770-fache der Periodendauer der dem Übergang zwischen den beiden Hyperfeinstrukturniveaus des Grundzustandes von Atomen des Caesium-Isotops <sup>133</sup>Cs entsprechenden Strahlung.
          </p>
        </div>
      </div>
    </div>
    <div class="columns">
      <div class="column is-one-third">
        <div class="box is-wordWrapBreakWord is-fullHeight has-text-centered">
          <h3>Stromstärke</h3>
          <p>
            <strong>Formelzeichen: </strong> <span v-katex="'I'"></span><br>
            <strong>Einheit: </strong> <span v-katex="'A'"></span> (Ampere)
          </p>
          <p>
            Stärke eines konstanten elektrischen Stromes, der, durch zwei parallele, geradlinige, unendlich lange und im Vakuum im Abstand von 1 Meter voneinander angeordnete Leiter von vernachlässigbar kleinem, kreisförmigem Querschnitt fließend, zwischen diesen Leitern pro Meter Leiterlänge die Kraft 2 * 10<sup>−7</sup> Newton hervorrufen würde.
          </p>
        </div>
      </div>
      <div class="column is-one-third">
        <div class="box is-wordWrapBreakWord is-fullHeight has-text-centered">
          <h3>Thermodynamische Temperatur</h3>
          <p>
            <strong>Formelzeichen: </strong> <span v-katex="'T'"></span><br>
            <strong>Einheit: </strong> <span v-katex="'K'"></span> (Sekunden)
          </p>
          <p>
            1/273,16 der thermodynamischen Temperatur des <a target="_blank" href="https://de.wikipedia.org/wiki/Tripelpunkt">Tripelpunkts</a> von Wasser genau definierter isotopischer Zusammensetzung.
          </p>
        </div>
      </div>
      <div class="column is-one-third">
        <div class="box is-wordWrapBreakWord is-fullHeight has-text-centered">
          <h3>Stoffmenge</h3>
          <p>
            <strong>Formelzeichen: </strong> <span v-katex="'tn'"></span><br>
            <strong>Einheit: </strong> <span v-katex="'mol'"></span> (Mol)
          </p>
          <p>
            Die Stoffmenge eines Systems, das aus ebenso vielen Einzelteilchen besteht, wie Atome in 12 Gramm des Kohlenstoff-Nuklids <sup>12</sup>C in ungebundenem Zustand enthalten sind. Bei Benutzung des Mol müssen die Einzelteilchen spezifiziert sein und können Atome, Moleküle, Ionen, Elektronen sowie andere Teilchen oder Gruppen solcher Teilchen genau angegebener Zusammensetzung sein.
          </p>
        </div>
      </div>
    </div>
    <div class="columns is-centered">
      <div class="column is-one-third">
        <div class="box is-wordWrapBreakWord is-fullHeight has-text-centered">
          <h3>Lichtstärke</h3>
          <p>
            <strong>Formelzeichen: </strong> <span v-katex="'I_V'"></span><br>
            <strong>Einheit: </strong> <span v-katex="'cd'"></span> (Candela)
          </p>
          <p>
            Die Lichtstärke in einer bestimmten Richtung einer Strahlungsquelle, die monochromatische Strahlung der Frequenz 540 * 10<sup>12</sup> Hz aussendet und deren Strahlstärke in dieser Richtung 1/683 Watt pro Steradiant beträgt.
          </p>
        </div>
      </div>
    </div>

    <h2 class="has-text-centered">Abgeleitete Einheiten</h2>
    <div class="box is-size-7-mobile">
      <div class="has-overflow-x-auto">
        <table class="table is-hoverable is-striped is-fullwidth">
          <thead>
            <tr>
              <th>Größe</th>
              <th>Einheit</th>
              <th>In Basiseinheiten</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Ebener Winkel</td>
              <td>Radiant (rad)</td>
              <td>m/m</td>
            </tr>
            <tr>
              <td>Raumwinkel</td>
              <td>Steradiant (sr)</td>
              <td>m<sup>2</sup>/m<sup>2</sup></td>
            </tr>
            <tr>
              <td>Frequenz</td>
              <td>Hertz (Hz)</td>
              <td>s<sup>−1</sup></td>
            </tr>
            <tr>
              <td>Kraft</td>
              <td>Newton (N)</td>
              <td>m * kg * s<sup>−2</sup></td>
            </tr>
            <tr>
              <td>Druck</td>
              <td>Pascal (Pa)</td>
              <td>m<sup>−1</sup> * kg * s<sup>−2</sup></td>
            </tr>
            <tr>
              <td>Energie, Arbeit, Wärmemenge</td>
              <td>Joule (J)</td>
              <td>m<sup>2</sup> * kg * s<sup>−2</sup></td>
            </tr>
            <tr>
              <td>Leistung</td>
              <td>Watt (W)</td>
              <td>m<sup>2</sup> * kg * s<sup>−3</sup></td>
            </tr>
            <tr>
              <td>Elektrische Ladung</td>
              <td>Coulomb (C)</td>
              <td>s * A</td>
            </tr>
            <tr>
              <td>Elektrische Spannung (elektrische Potentialdifferenz)</td>
              <td>Volt (V)</td>
              <td>m<sup>2</sup> * kg * s<sup>−3</sup> * A<sup>−1</sup></td>
            </tr>
            <tr>
              <td>Elektrische Kapazität</td>
              <td>Farad (F)</td>
              <td>m<sup>−2</sup> * kg<sup>−1</sup> * s<sup>4</sup> * A<sup>2</sup></td>
            </tr>
            <tr>
              <td>Elektrischer Widerstand</td>
              <td>Ohm (Ω)</td>
              <td>m<sup>2</sup> * kg * s<sup>−3</sup> * A<sup>−2</sup></td>
            </tr>
            <tr>
              <td>Elektrischer Leitwert</td>
              <td>Siemens (S)</td>
              <td>m<sup>−2</sup> * kg<sup>−1</sup> * s<sup>3</sup> * A<sup>2</sup></td>
            </tr>
            <tr>
              <td>Magnetischer Fluss</td>
              <td>Weber (Wb)</td>
              <td>m<sup>2</sup> * kg * s<sup>−2</sup> * A<sup>−1</sup></td>
            </tr>
            <tr>
              <td>Magnetische Flussdichte, Induktion</td>
              <td>Tesla (T)</td>
              <td>kg * s<sup>−2</sup> * A<sup>−1</sup></td>
            </tr>
            <tr>
              <td>Induktivität</td>
              <td>Henry (H)</td>
              <td>m<sup>2</sup> * kg * s<sup>−2</sup> * A<sup>−2</sup></td>
            </tr>
            <tr>
              <td>Celsius-Temperatur</td>
              <td>Grad Celsius (°C)</td>
              <td>K</td>
            </tr>
            <tr>
              <td>Lichtstrom</td>
              <td>Lumen (lm)</td>
              <td>cd</td>
            </tr>
            <tr>
              <td>Beleuchtungsstärke</td>
              <td>Lux (lx)</td>
              <td>m<sup>−2</sup> * cd</td>
            </tr>
            <tr>
              <td>Radioaktivität</td>
              <td>Becquerel (Bq)</td>
              <td>s<sup>−1</sup></td>
            </tr>
            <tr>
              <td>Energiedosis</td>
              <td>Gray (Gy)</td>
              <td>m<sup>2</sup> * s<sup>−2</sup></td>
            </tr>
            <tr>
              <td>Äquivalentdosis</td>
              <td>Sievert (Sv)</td>
              <td>m<sup>2</sup> * s<sup>−2</sup></td>
            </tr>
            <tr>
              <td>Katalytische Aktivität</td>
              <td>Katal (kat)</td>
              <td>s<sup>−1</sup> * mol</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'tables-si-units',
  metaInfo: {
    title: 'SI-Einheiten Tabelle | Elektrotechik-Rechner.at',
    meta: [
      {
        name: 'description',
        content: 'Das internationale Einheitensystem (SI) ist das am weitesten verbreitete Einheitensystem für physikalische Größen.'
      }
    ]
  },
  created () {
    this.$store.commit('updateNavTitle', { title: '<span class="fas fa-angle-left"></span> Zurück', route: 'tables' })
  }
}
</script>
